export type LogLevel = "INFO" | "WARN" | "ERROR" | "DEBUG";

export interface LogEntry {
  ts: string;
  level: LogLevel;
  event: string;
  data: any;
  cid: string; // Correlation ID for tracking
}

class Logger {
  private static instance: Logger;
  private logs: LogEntry[] = [];
  private maxLogs: number = 50;

  private constructor() {}

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  log(event: string, data: any = {}, level: LogLevel = "INFO") {
    const entry: LogEntry = {
      ts: new Date().toISOString(),
      level,
      event,
      data,
      cid: crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2),
    };

    // Console logging
    const consoleMethod = level === "ERROR" ? "error" : level === "WARN" ? "warn" : "log";
    console[consoleMethod](`[${level}] ${event}`, data);

    // Store in memory (last 50 entries)
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Emit event for UI updates
    window.dispatchEvent(new CustomEvent('log-entry', { detail: entry }));

    // Send to server (non-blocking)
    this.sendToServer(entry).catch(() => {
      // Silently fail - we don't want logging errors to break the app
    });

    return entry;
  }

  info(event: string, data?: any) {
    return this.log(event, data, "INFO");
  }

  warn(event: string, data?: any) {
    return this.log(event, data, "WARN");
  }

  error(event: string, data?: any) {
    return this.log(event, data, "ERROR");
  }

  debug(event: string, data?: any) {
    return this.log(event, data, "DEBUG");
  }

  getRecentLogs(count: number = 50): LogEntry[] {
    return this.logs.slice(-count);
  }

  clearLogs() {
    this.logs = [];
  }

  private async sendToServer(entry: LogEntry) {
    // Only send errors and warnings to server
    if (entry.level === "INFO" || entry.level === "DEBUG") {
      return;
    }

    try {
      await fetch("/api/telemetry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(entry),
      });
    } catch (error) {
      // Ignore telemetry errors
    }
  }
}

export const logger = Logger.getInstance();

// Helper functions for common operations
export function logScriptGeneration(topic: string, length: string, success: boolean, error?: string) {
  logger.log(
    success ? "SCRIPT_GENERATION_SUCCESS" : "SCRIPT_GENERATION_FAIL",
    { topic, length, error },
    success ? "INFO" : "ERROR"
  );
}

export function logVideoGeneration(segment: number, success: boolean, error?: string) {
  logger.log(
    success ? "VIDEO_GENERATION_SUCCESS" : "VIDEO_GENERATION_FAIL",
    { segment, error },
    success ? "INFO" : "ERROR"
  );
}

export function logAPICall(service: string, method: string, success: boolean, duration?: number, error?: string) {
  logger.log(
    "API_CALL",
    { service, method, success, duration, error },
    success ? "INFO" : "ERROR"
  );
}

export function logUserAction(action: string, details?: any) {
  logger.log("USER_ACTION", { action, ...details }, "INFO");
}